import { BrutusCaseRunDetailReadModel, BrutusJobGender, BrutusJobResult, BrutusReference, BrutusTestType, CaseRunClient, SystemFrequencies, BrutusHypothesisSystemResult } from "@app/api2/api";
import { CopyToClipboard } from "@app/components/CopyToClipboard";
import { BPBoxWithHeader } from "@app/components/React/Common/BPBoxWithHeader";
import { InfoBox } from "@app/components/React/Common/InfoBox";
import { toSci } from "@app/components/React/Common/Utils";
import { nanoid } from "nanoid";
import * as React from "react";
import { Grid, GridColumn, GridRow, Header, List, ListContent, ListHeader, ListItem, Segment, Tab, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, TabPane } from "@/components/bp/bp-ui";
import { PedigreeGraph } from "../../../RunTypes/Brutus/Pedigree/Graph";
import { BrutusResultsReport } from "./BrutusResultsReport";
import { ListDescription } from "semantic-ui-react";

interface IBrutusResultsProps {
  labId: number;
  caseId: number
  caseRunId: number;
  forPrint?: boolean;
}

export const BrutusResults: React.FunctionComponent<IBrutusResultsProps> = (props) => {
  const client: CaseRunClient = new CaseRunClient();
  const [systemsH1, setSystemsH1] = React.useState<BrutusHypothesisSystemResult[]>([])
  const [systemsH2, setSystemsH2] = React.useState<BrutusHypothesisSystemResult[]>()
  const [references, setReferences] = React.useState<BrutusReference[]>()
  const [frequencies, setFrequencies] = React.useState<SystemFrequencies[]>()
  const [kinship, setKinship] = React.useState<BrutusJobResult>()
  const [detail, setDetail] = React.useState<BrutusCaseRunDetailReadModel>()

  const panes = [
    { menuItem: 'Result', render: () => <TabPane><KinshipResults details={detail} model={kinship} frequencies={frequencies} references={references}></KinshipResults></TabPane> },
    { menuItem: 'Report', render: () => <TabPane><BrutusResultsReport details={detail} model={kinship} frequencies={frequencies} references={references}></BrutusResultsReport></TabPane> },
  ]

  React.useEffect(() => {
    async function fetch() {
      let data = await client.results(props.labId, props.caseId, props.caseRunId)
      setSystemsH1((data.detail as BrutusCaseRunDetailReadModel).results.h1.systems)
      setSystemsH2((data.detail as BrutusCaseRunDetailReadModel).results.h2.systems)
      setReferences((data.detail as BrutusCaseRunDetailReadModel).results.references)
      setFrequencies((data.detail as BrutusCaseRunDetailReadModel).results.frequencies)
      setKinship((data.detail as BrutusCaseRunDetailReadModel).results.result)
      setDetail((data.detail as BrutusCaseRunDetailReadModel))
    }
    fetch()
  }, [])

  return <Tab panes={panes} />
};

export interface IBrutusHypothesisResultsTableProps {
  hypothesis: number;
  systems: BrutusHypothesisSystemResult[]
  references: BrutusReference[]
  frequencies: SystemFrequencies[]
}

class BrutusHypothesisResultsTable extends React.PureComponent<IBrutusHypothesisResultsTableProps> {
  public render() {
    return (
      <Segment>
        <Header>{`H${this.props.hypothesis}`}</Header>
        <Table>
          <TableHeader></TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>System</TableCell>
              {this.props.references?.map(ref => (
                <TableCell colSpan={2} key={nanoid(3)}>
                  {ref.name}
                </TableCell>
              )
              )}
              <TableCell>LR</TableCell>
              <TableCell>Formula</TableCell>

            </TableRow>
            {this.props.systems?.map(s => (
              <React.Fragment key={s.systemName}>
                <TableRow>
                  <TableCell>{s.systemName}</TableCell>
                  {this.props.references?.map(ref => {
                    return (ref.systems.find(refS => refS.systemName == s.systemName)?.alleles ?? [{ value: '' }, { value: '' }]).map(al => (
                      <TableCell key={nanoid(3)}>
                        {al.value}
                      </TableCell>
                    ))
                  }
                  )}
                  <TableCell>{s.value}</TableCell>
                  <TableCell rowSpan={3}>{s.formula}</TableCell>
                  {/* <TableCell>{s.alleleToVarName.map(a => `${a.varName}=${a.allele}`).join(' ')}</TableCell> */}
                </TableRow>
                <TableRow>
                  <TableCell>Symbol</TableCell>
                  {this.props.references?.map(ref => {
                    return ref.systems.find(refS => refS.systemName == s.systemName)?.alleles.map(al => (
                      <TableCell key={nanoid(3)}>
                        {s.alleleToVarName.find(x => x.allele == al.value)?.varName}
                      </TableCell>
                    ))
                  }
                  )}
                </TableRow>
                <TableRow>
                  <TableCell>Frequency</TableCell>
                  {this.props.references?.map(ref => {
                    return ref.systems.find(refS => refS.systemName == s.systemName)?.alleles.map(al => (
                      <TableCell key={nanoid(3)}>
                        {this.props.frequencies?.find(x => x.name == s.systemName)?.frequencies.find(x => x.allele == al.value)?.frequency}
                      </TableCell>
                    ))
                  }
                  )}
                </TableRow>

                {/* <TableRow>
                  <TableCell>{s.systemName}</TableCell>
                  <TableCell>{s.value}</TableCell>
                  <TableCell>{s.formula}</TableCell>
                  <TableCell>{s.alleleToVarName.map(a => `${a.varName}=${a.allele}`).join(' ')}</TableCell>
                </TableRow> */}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Segment>
    );
  }
}

interface IKinshipResultsProps {
  details: BrutusCaseRunDetailReadModel;
  model: BrutusJobResult;
  references: BrutusReference[]
  frequencies: SystemFrequencies[]
}

const KinshipResults: React.FunctionComponent<IKinshipResultsProps> = (props) => {
  const t = window.translation;
  // const references = props.details.h1?.references ?? props.details.references
  const references = props.references
  const testedReferences = references?.filter(x => !x.isUntested)
  const brutusModel = props.details
  const isBrutusFreeform = brutusModel?.testType == BrutusTestType.Freeform;
  const isUhr = brutusModel?.testType == BrutusTestType.UHR;
  const isKinship = brutusModel?.testType == BrutusTestType.Kinship;

  const inputRef = React.createRef<HTMLDivElement>()

  const [hypDefBase64, setHypDefBase64] = React.useState<string>()
  const [h1Base64, setH1Base64] = React.useState<string>()
  const [h2Base64, setH2Base64] = React.useState<string>()
  // const h2Base64 = React.useState<string>()


  return <Grid>
    <GridRow columns={1}>
      <GridColumn>
        <InfoBox compact items={[
          { label: t.brutus.combRelIndex, description: toSci(props.model?.totalProbability, 4, '0') },
          { label: `H1 ${t.caseRunView.likelihood}`, description: toSci(props.model?.H1Probability, 4, '0') },
          { label: `H2 ${t.caseRunView.likelihood}`, description: toSci(props.model?.H2Probability, 4, '0') },
        ]} />

        <Table verticalAlign={'top'} fixed singleLine>
          <TableHeader>
            <TableRow>
              <TableHeaderCell width={2}>{t.common.system}</TableHeaderCell>
              {testedReferences?.map(ref => (
                <TableHeaderCell colSpan={2} key={`hc_${nanoid(3)}`}>
                  {ref.name}
                </TableHeaderCell>
              ))}
              <TableHeaderCell width={2}>RI</TableHeaderCell>
              {/* <TableHeaderCell width={6}>{t.common.formula}</TableHeaderCell> */}
            </TableRow>
          </TableHeader>
          <TableBody>
            {props.model?.systems.map(sys => {
              const probs = [
                { label: t.brutus.index, description: toSci(sys.totalProbability, 4) },
                { label: `H1`, description: toSci(sys.H1Probability, 4) },
                { label: `H2`, description: toSci(sys.H2Probability, 4) },
              ];
              const formulas = [
                { label: t.brutus.expression, description: sys.totalFormula },
                { label: `H1`, description: sys.H1Formula },
                { label: `H2`, description: sys.H2Formula },
              ];
              return (
                <React.Fragment key={sys.systemName}>
                  <TableRow>
                    <TableCell style={{ borderTop: '1px solid black' }}>{sys.systemName}</TableCell>
                    {testedReferences?.map(ref => {
                      var refS = ref.systems.find(x => x.systemName == sys.systemName);
                      return refS?.alleles.map(a => {
                        return <TableCell key={sys.systemName + nanoid(3)} style={{ borderTop: '1px solid black' }}>{a.value}</TableCell>;
                      })
                    })}
                    <TableCell style={{ borderTop: '1px solid black' }} rowSpan={3}>
                      <List divided>
                        {probs.map(c => (
                          <ListItem key={c.label}>
                            <ListContent>
                              <ListHeader as={'div'}>{c.label}</ListHeader>
                              <ListDescription>
                                {c.description}
                              </ListDescription>
                            </ListContent>
                          </ListItem>
                        ))}
                      </List>
                    </TableCell>
                    {/* <TableCell style={{ borderTop: '1px solid black' }} rowSpan={3}>
                      <List divided>
                        {formulas.map(c => (
                          <ListItem key={`form_${c.label}`}>
                            <ListContent>
                              <ListHeader as={'div'}>{c.label}</ListHeader>
                              <ListDescription>
                                {c.description}
                              </ListDescription>
                            </ListContent>
                          </ListItem>
                        ))}
                      </List>
                    </TableCell> */}
                  </TableRow>
                  <TableRow>
                    <TableCell>{t.common.symbol}</TableCell>
                    {testedReferences?.map(ref => {
                      return ref.systems.find(x => x.systemName == sys.systemName)?.alleles.map(al => (
                        <TableCell key={`sym_${nanoid(3)}`}>
                          {sys.variableMapping.find(x => x.allele == al.value)?.varName}
                        </TableCell>
                      ))
                    }
                    )}
                  </TableRow>
                  <TableRow>
                    <TableCell>{t.common.frequency}</TableCell>
                    {testedReferences?.map(ref => {
                      return ref.systems.find(refS => refS.systemName == sys.systemName)?.alleles.map(al => (
                        <TableCell key={`freq_${nanoid(3)}`}>
                          {toSci(props.frequencies?.find(x => x.name == sys.systemName)?.frequencies.find(x => x.allele == al.value)?.frequency, 5)}
                        </TableCell>
                      ))
                    }
                    )}
                  </TableRow>
                  {formulas.map(f => (
                    <TableRow key={f.label}>
                      <TableCell verticalAlign="top">{f.label}</TableCell>
                      <TableCell verticalAlign="top" colSpan={testedReferences.length * 2 + 1}>
                        <CopyToClipboard>
                          {f.description}
                        </CopyToClipboard>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* <TableRow>
                    <TableCell verticalAlign="top">H1 {t.brutus.expression}</TableCell>
                    <TableCell verticalAlign="top" colSpan={testedReferences.length * 2 + 1}>{sys.h1Formula}</TableCell>
                  </TableRow> */}
                  {/* <TableRow>
                    <TableCell verticalAlign="top">H2 {t.brutus.expression}</TableCell>
                    <TableCell verticalAlign="top" colSpan={testedReferences.length * 2 + 1}>{sys.h2Formula}</TableCell>
                  </TableRow> */}
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      </GridColumn>
    </GridRow>
    <GridRow columns={2}>
      <GridColumn>
        <BPBoxWithHeader header={t.brutus.pedigreeH1}>
          <PedigreeGraph
            onPng={(content) => { setH1Base64(content) }}
            readOnly={true}
            people={props.details?.results.h1.references.map(x => {
              return {
                displayName: x.name,
                imported: !x.isUntested,
                missing: false,
                personId: x.id.toString(),
                sex: x.gender == BrutusJobGender.Male ? "M" : "F",
                sampleName: x.name,
                systems: []
              }
            }) ?? []}
            relations={props.details?.results.h1.relations.map(x => {
              return {
                relationId: `h1a${x.ancestorId}d${x.descendantId}`,
                ancestor: x.ancestorId.toString(),
                descendant: x.descendantId.toString(),
                questioned: false
              }
            }) ?? []}></PedigreeGraph>
        </BPBoxWithHeader>
      </GridColumn>
      <GridColumn>
        <BPBoxWithHeader header={t.brutus.pedigreeH2}>
          <PedigreeGraph
            onPng={(content) => { setH2Base64(content) }}
            readOnly={true}
            people={props.details?.results.h2.references.map(x => {
              return {
                displayName: x.name,
                imported: !x.isUntested,
                missing: false,
                personId: x.id.toString(),
                sex: x.gender == BrutusJobGender.Male ? "M" : "F",
                sampleName: x.name,
                systems: []
              }
            }) ?? []}
            relations={props.details?.results.h2.relations.map(x => {
              return {
                relationId: `h2a${x.ancestorId}d${x.descendantId}`,
                ancestor: x.ancestorId.toString(),
                descendant: x.descendantId.toString(),
                questioned: false
              }
            }) ?? []}></PedigreeGraph>
        </BPBoxWithHeader>
      </GridColumn>
    </GridRow>
  </Grid>;
};




